import { Alert, DatePicker, Input, Modal } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { useCreateSavingsGoalItem } from "../../api/api";
import { useUpdateSavingsGoalItem } from "../../api/api.update";
import { useCurrencyContext } from "../../providers/currency.provider";
import { ISavingsGoalItem } from "../../types/types";

/**
 * Add or edit a savings-goal item (funds the user has secured toward a goal).
 * When `item` is provided the dialog is in edit mode.
 */
export default function GoalItemDialog({
  open,
  groupId,
  goalId,
  item,
  onCancel,
  onSuccess,
  onError,
}: {
  open: boolean;
  groupId?: string;
  goalId?: string;
  item?: ISavingsGoalItem;
  onCancel: () => void;
  onSuccess: () => void;
  onError: () => void;
}) {
  const { currency } = useCurrencyContext();

  const isEdit = !!item;

  const {
    mutateAsync: createAsync,
    error: createError,
    isPending: isCreating,
  } = useCreateSavingsGoalItem(groupId, goalId);
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateSavingsGoalItem(groupId, goalId);

  // Memoized so `dayjs()` isn't recreated every render — a changing
  // initialValues combined with enableReinitialize causes an infinite loop.
  const initialValues = useMemo(
    () => ({
      amount: item?.amount ?? "",
      itemDate: item?.itemDate ? dayjs(item.itemDate) : dayjs(),
      note: item?.note ?? "",
    }),
    [item],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Please enter a valid number")
        .positive("Amount must be greater than zero")
        .required("Please enter an amount"),
      itemDate: Yup.mixed().required("Please select a date"),
    }),
    onSubmit: async (values) => {
      const payloadDate = (values.itemDate as dayjs.Dayjs).format("YYYY-MM-DD");
      try {
        if (isEdit) {
          await updateAsync({
            itemId: item!.id,
            amount: +values.amount,
            itemDate: payloadDate,
            note: values.note,
          });
        } else {
          await createAsync({
            amount: +values.amount,
            itemDate: payloadDate,
            note: values.note,
          });
        }
        onSuccess();
      } catch {
        onError();
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Modal
        title={isEdit ? "Edit Contribution" : "Add Contribution"}
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText={isEdit ? "Save" : "Add"}
        okButtonProps={{ loading: isCreating || isUpdating }}
      >
        <div className="flex flex-col gap-4 my-6">
          {createError && (
            <Alert type="error" message="Whoops! Please try again." />
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">Amount *</label>
            <Input
              type="number"
              prefix={currency}
              placeholder="e.g., 500"
              {...formik.getFieldProps("amount")}
              status={formik.errors.amount ? "error" : undefined}
            />
            {formik.errors.amount && (
              <p className="text-xs text-red-500 mt-1">
                {formik.errors.amount}
              </p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium mb-2">Date *</label>
            <DatePicker
              className="w-full"
              value={formik.values.itemDate as dayjs.Dayjs}
              onChange={(date) => formik.setFieldValue("itemDate", date)}
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium mb-2">Note</label>
            <Input
              placeholder="Optional note"
              {...formik.getFieldProps("note")}
            />
          </div>
        </div>
      </Modal>
    </form>
  );
}
